import {
  getServerErrorCode,
  getServerErrorMessage,
} from '@/utils/common/serverError';

const GENERIC_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Stable auth copy for 401s. The server's 401 message is not display-safe
 * (prod token/JWT copy varies), so we never surface it — see below.
 */
const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';

/** Read the HTTP status from either carrier (RTK `{ status }`, axios `{ response: { status } }`). */
const getStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const e = error as Record<string, any>;
  const status = e.status ?? e.response?.status;
  return typeof status === 'number' ? status : undefined;
};

/**
 * Extract a human-readable message from a todo-lists mutation failure.
 *
 * The lifestyle controller now returns the RES-97 unified envelope
 * `{ success: false, error: { code, status, message, description?, details? } }`
 * (older builds emitted a bare `{ error: '<string>' }` / `{ message }` body).
 * We delegate parsing to the shared, dual-shape readers in
 * `@/utils/common/serverError` so both wire shapes — surfaced through the RTK
 * Query (`{ status, data }`) or axios (`{ response: { data } }`) carrier — are
 * handled uniformly.
 *
 * Sub-case handling:
 *  - The 409s (`list_archived` vs `no_current_occurrence`) that the create/edit
 *    + apply-now flows can hit share the wire code `already-exists`, so they are
 *    distinguished only by their `message` — which is exactly what the inline
 *    error renders, preserving the pass-through UX.
 *  - 401s are keyed on the code (`invalid-token`) or status, NEVER the message,
 *    and always render stable session-expired copy.
 *
 * Falls back to a generic message so the caller can always render a
 * non-crashing inline error.
 */
export function extractTodoListError(error: unknown): string {
  if (getServerErrorCode(error) === 'invalid-token' || getStatus(error) === 401) {
    return SESSION_EXPIRED_MESSAGE;
  }
  return getServerErrorMessage(error) ?? GENERIC_MESSAGE;
}
